import { useEffect, useState, useRef, useMemo } from 'react';
import { useCustomFields, CustomFieldInputs } from '@/components/CustomFieldInputs';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/components/AppLayout';
import { useSendBlockModal } from '@/contexts/SendBlockModalContext';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useAutoSave } from '@/hooks/useAutoSave';
import { ClientDialog } from '@/components/ClientDialog';
import { ItemDialog } from '@/components/ItemDialog';
import { format } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Package,
  X,
  Plus,
  Trash2,
  Building2,
  CalendarIcon,
  GripVertical,
  Upload,
  File,
  Loader2,
  Check,
  Send,
  Truck,
} from 'lucide-react';
import {
  purchaseOrderApi,
  clientApi,
  itemApi,
  type PurchaseOrder,
  type CreatePurchaseOrderData,
  type LineItem,
  type Client,
  type Item,
} from '@/lib/api';
import { formatAmount, getCurrencySymbol } from '@/lib/currency';
import { MarkupModal } from '@/components/Modals/MarkupModal';
import type { MarkupAppliedTo } from '@/lib/api';

// ============================================================================
// SORTABLE COMPONENTS
// ============================================================================

interface SortableItemRowProps {
  item: LineItem;
  sectionIndex: number;
  itemIndex: number;
  items: Item[];
  activeItemInput: string | null;
  setActiveItemInput: (id: string | null) => void;
  setShowItemDialog: (show: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateLineItem: (sectionIndex: number, itemIndex: number, field: keyof LineItem, value: any) => void;
  selectItemFromLibrary: (sectionIndex: number, itemIndex: number, item: Item) => void;
  removeLineItem: (sectionIndex: number, itemIndex: number) => void;
}

function SortableItemRow({
  item,
  sectionIndex,
  itemIndex,
  items,
  activeItemInput,
  setActiveItemInput,
  setShowItemDialog,
  updateLineItem,
  selectItemFromLibrary,
  removeLineItem,
}: SortableItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const inputId = `${sectionIndex}-${itemIndex}`;
  const filteredItems = items
    .filter((i) => i.name.toLowerCase().includes(item.name.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <tr ref={setNodeRef} style={style} className="border-t align-top">
      <td className="p-0 w-8 border-r">
        <div
          {...attributes}
          {...listeners}
          className="h-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      </td>
      <td className="p-0 border-r relative flex flex-col">
        <Input
          placeholder="Item name..."
          value={item.name}
          onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'name', e.target.value)}
          onFocus={() => setActiveItemInput(inputId)}
          onBlur={() => setTimeout(() => setActiveItemInput(null), 150)}
          className="border-0 shadow-none h-8 px-3 rounded-none flex-shrink-0"
        />
        <Textarea
          placeholder="Description..."
          value={item.description}
          onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'description', e.target.value)}
          className="border-0 shadow-none px-3 py-2 rounded-none bg-purple-500/5 text-sm resize-none flex-1"
          rows={3}
        />
        {activeItemInput === inputId && (
          <div className="absolute top-8 left-0 w-full mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-64 overflow-auto">
            <div
              className="px-3 py-2 hover:bg-accent cursor-pointer flex items-center gap-1 text-primary border-b text-sm"
              onClick={() => {
                setActiveItemInput(null);
                setShowItemDialog(true);
              }}
            >
              <Plus className="h-3 w-3" />
              add new item to catalog
            </div>
            {filteredItems.map((libraryItem) => (
              <div
                key={libraryItem.id}
                className="px-3 py-2 hover:bg-accent cursor-pointer"
                onClick={() => selectItemFromLibrary(sectionIndex, itemIndex, libraryItem)}
              >
                <div className="text-sm">{libraryItem.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatAmount(libraryItem.rate)}
                </div>
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="p-0 border-r">
        <Input
          type="number"
          min="0"
          step="0.01"
          value={item.quantity}
          onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'quantity', e.target.value)}
          className="border-0 shadow-none h-8 px-3 text-right rounded-none"
        />
      </td>
      <td className="p-0 border-r">
        <Input
          type="number"
          step="0.01"
          value={item.rate}
          onChange={(e) => updateLineItem(sectionIndex, itemIndex, 'rate', e.target.value)}
          className="border-0 shadow-none h-8 px-3 text-right rounded-none"
        />
      </td>
      <td className="p-0 relative">
        <Input
          type="text"
          value={formatAmount(item.amount)}
          disabled
          className="border-0 shadow-none h-8 px-3 text-right rounded-none bg-muted/50 disabled:opacity-100 disabled:cursor-default"
        />
        <button
          type="button"
          onClick={() => removeLineItem(sectionIndex, itemIndex)}
          className="absolute right-0 top-0 translate-x-full p-2 text-muted-foreground hover:text-destructive transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

interface SortableSectionProps {
  sectionId: string;
  sectionIndex: number;
  sectionName: string;
  sectionItems: LineItem[];
  lineItemsCount: number;
  items: Item[];
  activeItemInput: string | null;
  setActiveItemInput: (id: string | null) => void;
  setShowItemDialog: (show: boolean) => void;
  updateSectionName: (sectionIndex: number, name: string) => void;
  removeSection: (sectionIndex: number) => void;
  addLineItem: (sectionIndex: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateLineItem: (sectionIndex: number, itemIndex: number, field: keyof LineItem, value: any) => void;
  selectItemFromLibrary: (sectionIndex: number, itemIndex: number, item: Item) => void;
  removeLineItem: (sectionIndex: number, itemIndex: number) => void;
}

function SortableSection({
  sectionId,
  sectionIndex,
  sectionName,
  sectionItems,
  lineItemsCount,
  items,
  activeItemInput,
  setActiveItemInput,
  setShowItemDialog,
  updateSectionName,
  removeSection,
  addLineItem,
  updateLineItem,
  selectItemFromLibrary,
  removeLineItem,
}: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="border overflow-visible rounded-lg">
        {(lineItemsCount > 1 || sectionName) && (
          <div className="relative bg-purple-500/5 border-b flex items-center">
            {lineItemsCount > 1 && (
              <div
                {...attributes}
                {...listeners}
                className="h-10 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                <GripVertical className="h-4 w-4" />
              </div>
            )}
            <Input
              placeholder="Section name (optional)"
              value={sectionName}
              onChange={(e) => updateSectionName(sectionIndex, e.target.value)}
              className={`border-0 shadow-none h-10 px-3 rounded-none rounded-t-lg bg-transparent flex-1 ${lineItemsCount > 1 ? 'pl-0' : ''}`}
            />
            {lineItemsCount > 1 && (
              <button
                type="button"
                onClick={() => removeSection(sectionIndex)}
                className="absolute right-0 top-0 translate-x-full p-2 text-muted-foreground hover:text-destructive transition-colors h-10 flex items-center"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr className="text-xs text-muted-foreground">
              <th className="w-8"></th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-right px-3 py-2 w-20">Qty</th>
              <th className="text-right px-3 py-2 w-24">Rate</th>
              <th className="text-right px-3 py-2 w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            <SortableContext
              items={sectionItems.map((item) => item.id!)}
              strategy={verticalListSortingStrategy}
            >
              {sectionItems.map((item, itemIndex) => (
                <SortableItemRow
                  key={item.id}
                  item={item}
                  sectionIndex={sectionIndex}
                  itemIndex={itemIndex}
                  items={items}
                  activeItemInput={activeItemInput}
                  setActiveItemInput={setActiveItemInput}
                  setShowItemDialog={setShowItemDialog}
                  updateLineItem={updateLineItem}
                  selectItemFromLibrary={selectItemFromLibrary}
                  removeLineItem={removeLineItem}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
        <div className="border-t px-3 py-2">
          <button
            type="button"
            onClick={() => addLineItem(sectionIndex)}
            className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            <Plus className="h-3 w-3" />
            Add item
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function createEmptyLineItem(): LineItem {
  return {
    id: crypto.randomUUID(),
    name: '',
    code: '',
    description: '',
    quantity: 1,
    rate: 0,
    tax_ids: [],
    amount: 0,
  };
}

export function CreatePurchaseOrder() {
  const { id: purchaseOrderIdFromParams } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness, user } = useApp();
  const { showEmailVerificationModal } = useSendBlockModal();
  const businessId = currentBusiness?.id || '';

  // Phase management
  const isEditMode = !!purchaseOrderIdFromParams;
  // Track if we started this session creating a new document (vs editing existing)
  // This stays true even after the URL changes to include the new draft's ID
  const startedAsNew = useRef(!purchaseOrderIdFromParams);
  const [phase, setPhase] = useState<'vendor-selection' | 'full-form'>(
    purchaseOrderIdFromParams ? 'full-form' : 'vendor-selection'
  );

  // Purchase order state
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [creatingPurchaseOrder, setCreatingPurchaseOrder] = useState(false);

  // Reference data (vendors are stored as clients)
  const [vendors, setVendors] = useState<Client[]>([]);
  const [items, setItems] = useState<Item[]>([]);

  // Form state
  const [selectedVendor, setSelectedVendor] = useState<Client | null>(null);
  const [issuedDate, setIssuedDate] = useState(new Date().toISOString().split('T')[0]);
  const [lineItems, setLineItems] = useState<[string, LineItem[]][]>([
    ['', [createEmptyLineItem()]]
  ]);
  const [markupType, setMarkupType] = useState<'dollars' | 'percent'>('percent');
  const [markupValue, setMarkupValue] = useState(0);
  const [markupAppliedTo, setMarkupAppliedTo] = useState<MarkupAppliedTo>('none');
  const [noteForVendor, setNoteForVendor] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  // Shipping
  const [shipEnabled, setShipEnabled] = useState(false);
  const [shipFee, setShipFee] = useState<number | null>(null);
  const [shipDate, setShipDate] = useState<string>('');
  const [shipVia, setShipVia] = useState('');
  const [shipTrackingNumber, setShipTrackingNumber] = useState('');

  // Custom fields from template
  const { customFields, customFieldValues, setFieldValue, toggleField, populateValues: populateCustomFieldValues } = useCustomFields(businessId, 'purchase_order');

  // Attachments
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showChangeVendorDialog, setShowChangeVendorDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [changeVendorSearchQuery, setChangeVendorSearchQuery] = useState('');
  const [activeItemInput, setActiveItemInput] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [discardingDraft, setDiscardingDraft] = useState(false);
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
  const initialLoadComplete = useRef(false);
  const initialDataRef = useRef<string | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getSectionId = (index: number) => `section-${index}`;
  const sectionIds = lineItems.map((_, index) => getSectionId(index));

  // Load reference data
  useEffect(() => {
    if (businessId) {
      clientApi.list(businessId).then((r) => r.data && setVendors(r.data));
      itemApi.list(businessId).then((r) => r.data && setItems(r.data.results));
    }
  }, [businessId]);

  // Load purchase order if ID in URL
  useEffect(() => {
    if (purchaseOrderIdFromParams && businessId) {
      purchaseOrderApi.get(purchaseOrderIdFromParams).then((r) => {
        if (r.data) {
          populateFormFromPurchaseOrder(r.data);
        }
      });
    }
  }, [purchaseOrderIdFromParams, businessId]);

  // Set selected vendor when vendors are loaded and purchase order is loaded
  useEffect(() => {
    if (purchaseOrder && vendors.length > 0 && !selectedVendor) {
      const vendor = vendors.find((v) => v.id === purchaseOrder.vendor);
      if (vendor) {
        setSelectedVendor(vendor);
      }
    }
  }, [purchaseOrder, vendors, selectedVendor]);

  const populateFormFromPurchaseOrder = (po: PurchaseOrder) => {
    setPurchaseOrder(po);
    setIssuedDate(po.issued_date || new Date().toISOString().split('T')[0]);
    setLineItems(po.line_items.length > 0 ? po.line_items : [['', [createEmptyLineItem()]]]);
    setNoteForVendor(po.note_for_vendor || '');
    setInternalNotes(po.internal_notes || '');

    const markupDollars = parseFloat(po.markup_dollars);
    const markupPercent = parseFloat(po.markup_percent);
    if (markupDollars > 0) {
      setMarkupType('dollars');
      setMarkupValue(markupDollars);
    } else if (markupPercent > 0) {
      setMarkupType('percent');
      setMarkupValue(markupPercent);
    }
    setMarkupAppliedTo(po.markup_applied_to || 'none');

    // Shipping
    setShipEnabled(po.ship_enabled || false);
    setShipFee(po.ship_fee ? parseFloat(po.ship_fee) : null);
    setShipDate(po.ship_date || '');
    setShipVia(po.ship_via || '');
    setShipTrackingNumber(po.ship_tracking_number || '');
    if (!startedAsNew.current) populateCustomFieldValues(po.custom_field_values);

    setPhase('full-form');
  };

  // Handle vendor selection (Phase 1 -> Phase 2)
  const handleVendorSelect = async (vendor: Client) => {
    if (creatingPurchaseOrder) return;
    setCreatingPurchaseOrder(true);

    const response = await purchaseOrderApi.create({
      business: businessId,
      vendor: vendor.id,
      issued_date: issuedDate,
      line_items: [['', []]],
    });

    if (response.data) {
      nav(`/purchase-orders/${response.data.id}/edit`);
    }

    setCreatingPurchaseOrder(false);
  };

  // Handle vendor change (in Phase 2)
  const handleChangeVendor = async (vendor: Client) => {
    if (!purchaseOrder) return;

    const response = await purchaseOrderApi.update(purchaseOrder.id, { business: businessId, vendor: vendor.id });
    if (response.data) {
      setPurchaseOrder(response.data);
      setSelectedVendor(vendor);
    }
    setShowChangeVendorDialog(false);
    setChangeVendorSearchQuery('');
  };

  // Auto-save data
  const autoSaveData = useMemo(() => {
    if (!purchaseOrder?.id) return null;

    return {
      business: businessId,
      vendor: selectedVendor?.id,
      issued_date: issuedDate,
      line_items: lineItems,
      note_for_vendor: noteForVendor,
      internal_notes: internalNotes,
      markup_dollars: markupType === 'dollars' ? markupValue : 0,
      markup_percent: markupType === 'percent' ? markupValue : 0,
      markup_applied_to: markupAppliedTo,
      ship_enabled: shipEnabled,
      ship_fee: shipEnabled ? shipFee : null,
      ship_date: shipEnabled ? (shipDate || null) : null,
      ship_via: shipEnabled ? shipVia : '',
      ship_tracking_number: shipEnabled ? shipTrackingNumber : '',
      custom_field_values: customFieldValues,
    };
  }, [
    businessId,
    purchaseOrder?.id,
    selectedVendor?.id,
    issuedDate,
    lineItems,
    noteForVendor,
    internalNotes,
    markupType,
    markupValue,
    markupAppliedTo,
    shipEnabled,
    shipFee,
    shipDate,
    shipVia,
    shipTrackingNumber,
    customFieldValues,
  ]);

  const { isSaving, lastSaved, saveNow } = useAutoSave({
    data: autoSaveData,
    onSave: async (data) => {
      if (!purchaseOrder?.id || !data) return;
      const response = await purchaseOrderApi.update(purchaseOrder.id, data as Partial<CreatePurchaseOrderData>);
      if (response.data) {
        setPurchaseOrder(response.data);
      }
    },
    // Only auto-save for NEW documents to avoid race condition with discard
    enabled: phase === 'full-form' && !!purchaseOrder?.id && startedAsNew.current,
    delay: 1500,
  });

  // Track when user makes changes (after initial load)
  useEffect(() => {
    // For new documents, track via auto-save's lastSaved
    if (startedAsNew.current && lastSaved && initialLoadComplete.current) {
      setHasUserMadeChanges(true);
    }
  }, [lastSaved]);

  // For existing documents, track changes by comparing to initial data
  useEffect(() => {
    if (startedAsNew.current || !initialLoadComplete.current || !autoSaveData) return;

    const currentData = JSON.stringify(autoSaveData);
    if (initialDataRef.current && currentData !== initialDataRef.current) {
      setHasUserMadeChanges(true);
    }
  }, [autoSaveData]);

  // Mark initial load as complete after purchase order is loaded
  useEffect(() => {
    if (purchaseOrder && !initialLoadComplete.current) {
      // Delay to allow form to settle before capturing initial state
      const timer = setTimeout(() => {
        initialLoadComplete.current = true;
        // Capture initial data for existing documents (for change detection)
        if (!startedAsNew.current && autoSaveData) {
          initialDataRef.current = JSON.stringify(autoSaveData);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [purchaseOrder, autoSaveData]);

  // Line item management
  const addLineItem = (sectionIndex: number) => {
    const newLineItems = [...lineItems];
    newLineItems[sectionIndex][1].push(createEmptyLineItem());
    setLineItems(newLineItems);
  };

  const removeLineItem = (sectionIndex: number, itemIndex: number) => {
    const newLineItems = [...lineItems];
    const sectionItems = newLineItems[sectionIndex][1].filter((_, i) => i !== itemIndex);
    if (sectionItems.length === 0) {
      sectionItems.push(createEmptyLineItem());
    }
    newLineItems[sectionIndex][1] = sectionItems;
    setLineItems(newLineItems);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateLineItem = (sectionIndex: number, itemIndex: number, field: keyof LineItem, value: any) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[sectionIndex][1][itemIndex] };

    if (field === 'name' || field === 'description' || field === 'code') {
      (item[field] as string) = value;
    } else if (field === 'quantity' || field === 'rate') {
      const numValue = parseFloat(value) || 0;
      (item[field] as number) = numValue;
      item.amount = item.quantity * item.rate;
    } else if (field === 'tax_ids') {
      item.tax_ids = value;
    }

    newLineItems[sectionIndex][1][itemIndex] = item;
    setLineItems(newLineItems);
  };

  const selectItemFromLibrary = (sectionIndex: number, itemIndex: number, selectedItem: Item) => {
    const newLineItems = [...lineItems];
    const item = { ...newLineItems[sectionIndex][1][itemIndex] };

    item.name = selectedItem.name;
    item.code = selectedItem.code || '';
    item.description = selectedItem.description;
    item.quantity = parseFloat(selectedItem.default_quantity) || 1;
    item.rate = parseFloat(selectedItem.rate) || 0;
    item.tax_ids = selectedItem.taxes;
    item.amount = item.quantity * item.rate;
    item.quantity_label_text = selectedItem.quantity_label_text;

    newLineItems[sectionIndex][1][itemIndex] = item;
    setLineItems(newLineItems);
    setActiveItemInput(null);
  };

  const addSection = (name: string = '') => {
    setLineItems([...lineItems, [name, [createEmptyLineItem()]]]);
  };

  const updateSectionName = (sectionIndex: number, name: string) => {
    const newLineItems = [...lineItems];
    newLineItems[sectionIndex][0] = name;
    setLineItems(newLineItems);
  };

  const removeSection = (sectionIndex: number) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== sectionIndex));
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId.startsWith('section-') && overId.startsWith('section-')) {
      return;
    }

    if (!activeId.startsWith('section-') && !overId.startsWith('section-')) {
      let activeSection = -1;
      let activeIndex = -1;
      let overSection = -1;
      let overIndex = -1;

      lineItems.forEach(([, sectionItems], sIndex) => {
        const aIndex = sectionItems.findIndex((item) => item.id === activeId);
        if (aIndex !== -1) {
          activeSection = sIndex;
          activeIndex = aIndex;
        }
        const oIndex = sectionItems.findIndex((item) => item.id === overId);
        if (oIndex !== -1) {
          overSection = sIndex;
          overIndex = oIndex;
        }
      });

      if (activeSection !== -1 && overSection !== -1 && activeSection !== overSection) {
        const newLineItems = [...lineItems];
        const [movedItem] = newLineItems[activeSection][1].splice(activeIndex, 1);
        newLineItems[overSection][1].splice(overIndex, 0, movedItem);

        if (newLineItems[activeSection][1].length === 0) {
          newLineItems[activeSection][1].push(createEmptyLineItem());
        }

        setLineItems(newLineItems);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    if (activeId.startsWith('section-') && overId.startsWith('section-')) {
      const oldIndex = sectionIds.indexOf(activeId);
      const newIndex = sectionIds.indexOf(overId);

      if (oldIndex !== -1 && newIndex !== -1) {
        setLineItems(arrayMove(lineItems, oldIndex, newIndex));
      }
      return;
    }

    if (!activeId.startsWith('section-') && !overId.startsWith('section-')) {
      let activeSection = -1;
      let activeIndex = -1;
      let overSection = -1;
      let overIndex = -1;

      lineItems.forEach(([, sectionItems], sIndex) => {
        const aIndex = sectionItems.findIndex((item) => item.id === activeId);
        if (aIndex !== -1) {
          activeSection = sIndex;
          activeIndex = aIndex;
        }
        const oIndex = sectionItems.findIndex((item) => item.id === overId);
        if (oIndex !== -1) {
          overSection = sIndex;
          overIndex = oIndex;
        }
      });

      if (activeSection === overSection && activeSection !== -1) {
        const newLineItems = [...lineItems];
        newLineItems[activeSection][1] = arrayMove(
          newLineItems[activeSection][1],
          activeIndex,
          overIndex
        );
        setLineItems(newLineItems);
      }
    }
  };

  // Calculations (memoized to prevent stale values)
  const subtotal = useMemo(() => {
    return lineItems.reduce((total, [, items]) => {
      return total + items.reduce((sectionTotal, item) => sectionTotal + item.amount, 0);
    }, 0);
  }, [lineItems]);

  // Calculate subtotals for each item type (for markup modal and calculations)
  const applicableSubtotals = useMemo(() => {
    const subtotals = { all: 0, labor: 0, product_parts: 0, other: 0 };
    lineItems.forEach(([, items]) => {
      items.forEach((item) => {
        const itemType = item.item_type || 'product_parts';
        subtotals.all += item.amount;
        if (itemType === 'labor') subtotals.labor += item.amount;
        if (itemType === 'product_parts') subtotals.product_parts += item.amount;
        if (itemType === 'other') subtotals.other += item.amount;
      });
    });
    return subtotals;
  }, [lineItems]);

  const markup = useMemo(() => {
    if (markupAppliedTo === 'none' || markupValue <= 0) return 0;
    const applicableSubtotal = markupAppliedTo === 'all'
      ? applicableSubtotals.all
      : applicableSubtotals[markupAppliedTo] || 0;
    if (applicableSubtotal <= 0) return 0;
    if (markupType === 'dollars') return markupValue;
    return applicableSubtotal * (markupValue / 100);
  }, [markupAppliedTo, markupValue, markupType, applicableSubtotals]);

  const shippingFee = shipEnabled && shipFee ? shipFee : 0;
  const total = subtotal + shippingFee + markup;

  // Helper for markup modal - uses memoized applicableSubtotals
  const getApplicableSubtotals = () => applicableSubtotals;

  // Get markup display text for the UI
  const getMarkupDisplayText = () => {
    if (markupAppliedTo === 'none' || markupValue <= 0) {
      return 'None';
    }
    const typeLabel = markupType === 'percent' ? `${markupValue}%` : formatAmount(markupValue);
    const targetLabel = { all: 'all items', labor: 'labor', product_parts: 'parts', other: 'other' }[markupAppliedTo];
    return `+${formatAmount(markup)} (${typeLabel} on ${targetLabel})`;
  };

  // Vendor filtering
  const filteredVendors = vendors.filter((vendor) =>
    vendor.billing_name.toLowerCase().includes(vendorSearchQuery.toLowerCase()) ||
    vendor.company_name.toLowerCase().includes(vendorSearchQuery.toLowerCase())
  );

  const filteredVendorsForChange = vendors.filter((vendor) =>
    vendor.billing_name.toLowerCase().includes(changeVendorSearchQuery.toLowerCase()) ||
    vendor.company_name.toLowerCase().includes(changeVendorSearchQuery.toLowerCase())
  );

  // Callbacks
  const handleVendorSaved = (vendor?: Client) => {
    if (vendor) {
      setVendors([...vendors, vendor]);
      handleVendorSelect(vendor);
    }
    setShowVendorDialog(false);
  };

  const handleVendorSavedForChange = (vendor?: Client) => {
    if (vendor) {
      setVendors([...vendors, vendor]);
      handleChangeVendor(vendor);
    }
    setShowVendorDialog(false);
  };

  const handleItemSaved = () => {
    itemApi.list(businessId).then((r) => r.data && setItems(r.data.results));
    setShowItemDialog(false);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !purchaseOrder?.id) return;

    setUploadingAttachment(true);
    for (const file of Array.from(files)) {
      await purchaseOrderApi.uploadAttachment(purchaseOrder.id, file);
    }

    const response = await purchaseOrderApi.get(purchaseOrder.id);
    if (response.data) {
      setPurchaseOrder(response.data);
    }
    setUploadingAttachment(false);
  };

  const handleRemoveAttachment = async (index: number) => {
    if (!purchaseOrder?.id) return;
    await purchaseOrderApi.deleteAttachment(purchaseOrder.id, index);
    const response = await purchaseOrderApi.get(purchaseOrder.id);
    if (response.data) {
      setPurchaseOrder(response.data);
    }
  };

  // Handle sending
  const handleSend = async () => {
    if (!purchaseOrder?.id) return;
    if (!user?.is_email_verified) {
      showEmailVerificationModal(user?.email || '');
      return;
    }
    await saveNow();
    const response = await purchaseOrderApi.send(purchaseOrder.id, { medium: 'email' });
    if (response.data) {
      setPurchaseOrder(response.data);
      nav(`/purchase-orders/${purchaseOrder.id}`);
    }
  };

  // Handle discard - for new documents, delete draft and go to list
  // For existing documents, just go back to detail page (changes are already auto-saved)
  const handleDiscard = async () => {
    if (!purchaseOrder) return;
    if (startedAsNew.current) {
      // New document created in this session - delete draft and go to list
      setDiscardingDraft(true);
      try {
        await purchaseOrderApi.delete(purchaseOrder.id);
        nav('/purchase-orders');
      } catch (error) {
        console.error('Failed to delete draft:', error);
        setDiscardingDraft(false);
        setShowDiscardDialog(false);
      }
    } else {
      // Editing existing document - just go back to detail page
      nav(`/purchase-orders/${purchaseOrder.id}`);
    }
  };

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          <AnimatePresence mode="wait">
            {phase === 'vendor-selection' ? (
            <motion.div
              key="vendor-selection"
              initial={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
                <button
                  onClick={() => nav('/purchase-orders')}
                  className="p-2 -ml-2 rounded-md hover:bg-muted transition-colors"
                  title="Cancel"
                >
                  <X className="h-5 w-5" />
                </button>
                <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
                <div className="h-6 w-px bg-border" />
                <span className="font-medium text-sm">{currentBusiness?.name}</span>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col items-center px-4 pt-12 pb-16">
                <div className="w-full max-w-md">
                  <h1 className="text-2xl mb-8 flex items-center gap-2">
                    {isEditMode ? 'Edit Purchase Order' : 'New Purchase Order'}
                    <Package className="h-5 w-5 text-purple-500" />
                  </h1>

                  <div className="space-y-5">
                    <div className="space-y-2 group">
                      <Label className="text-muted-foreground group-focus-within:text-foreground transition-colors">
                        Select a vendor
                      </Label>
                      <Input
                        placeholder="Search vendors..."
                        value={vendorSearchQuery}
                        onChange={(e) => setVendorSearchQuery(e.target.value)}
                        autoFocus
                      />
                    </div>

                    {vendors.length === 0 ? (
                      <div className="py-8 text-center">
                        <p className="text-muted-foreground mb-4">No vendors yet</p>
                        <Button
                          variant="outline"
                          onClick={() => setShowVendorDialog(true)}
                          disabled={creatingPurchaseOrder}
                          className="rounded-full"
                        >
                          + Add your first vendor
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="border rounded-lg divide-y max-h-[320px] overflow-y-auto">
                          {filteredVendors.length === 0 ? (
                            <div className="py-6 text-center text-muted-foreground text-sm">
                              No vendors match your search
                            </div>
                          ) : (
                            filteredVendors.map((vendor) => (
                              <div
                                key={vendor.id}
                                className={`px-4 py-3 cursor-pointer transition-colors ${creatingPurchaseOrder ? 'opacity-50' : 'hover:bg-muted/50'}`}
                                onClick={() => !creatingPurchaseOrder && handleVendorSelect(vendor)}
                              >
                                <div className="text-sm">{vendor.billing_name}</div>
                                {vendor.company_name && (
                                  <div className="text-xs text-muted-foreground">{vendor.company_name}</div>
                                )}
                              </div>
                            ))
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => setShowVendorDialog(true)}
                          disabled={creatingPurchaseOrder}
                          className="text-sm text-primary hover:underline disabled:opacity-50"
                        >
                          + Add new vendor
                        </button>
                      </>
                    )}

                    <div className="pt-4">
                      <Button
                        variant="outline"
                        onClick={() => nav('/purchase-orders')}
                        className="w-full rounded-full"
                      >
                        Go Back
                      </Button>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="full-form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
                <button
                  onClick={() => {
                    // Show modal if: new document in this session, OR user made changes
                    if (startedAsNew.current || hasUserMadeChanges) {
                      setShowDiscardDialog(true);
                    } else {
                      nav(`/purchase-orders/${purchaseOrder?.id}`);
                    }
                  }}
                  className="p-2 -ml-2 rounded-md hover:bg-muted transition-colors"
                  title="Cancel"
                >
                  <X className="h-5 w-5" />
                </button>
                <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
                <div className="h-6 w-px bg-border" />
                <span className="font-medium text-sm">{currentBusiness?.name}</span>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="max-w-3xl mx-auto space-y-6"
                >
                  <h1 className="text-2xl flex items-center gap-2">
                    {isEditMode ? 'Edit Purchase Order' : 'New Purchase Order'}
                    <Package className="h-5 w-5 text-purple-500" />
                  </h1>

                  {/* Vendor Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="space-y-2"
                  >
                    <Label className="text-muted-foreground">Vendor</Label>
                    {selectedVendor && (
                      <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p>{selectedVendor.billing_name}</p>
                            {selectedVendor.company_name && (
                              <p className="text-sm text-muted-foreground">{selectedVendor.company_name}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowChangeVendorDialog(true)}
                        >
                          Change
                        </Button>
                      </div>
                    )}
                  </motion.div>

                  {/* Issue Date */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-2"
                  >
                    <Label className="text-muted-foreground">Issue Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full max-w-xs justify-start text-left font-normal bg-purple-500/5"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {issuedDate ? format(new Date(issuedDate + 'T00:00:00'), 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={issuedDate ? new Date(issuedDate + 'T00:00:00') : undefined}
                          onSelect={(date) => date && setIssuedDate(date.toISOString().split('T')[0])}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </motion.div>

                  {/* Custom Fields */}
                  {customFields.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.22 }}
                    >
                      <CustomFieldInputs
                        customFields={customFields}
                        customFieldValues={customFieldValues}
                        setFieldValue={setFieldValue}
                        toggleField={toggleField}
                      />
                    </motion.div>
                  )}

                  {/* Line Items */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="space-y-4"
                  >
                    <Label className="text-muted-foreground">Line Items</Label>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
                        <div className="space-y-4">
                          {lineItems.map(([sectionName, sectionItems], sectionIndex) => (
                            <SortableSection
                              key={getSectionId(sectionIndex)}
                              sectionId={getSectionId(sectionIndex)}
                              sectionIndex={sectionIndex}
                              sectionName={sectionName}
                              sectionItems={sectionItems}
                              lineItemsCount={lineItems.length}
                              items={items}
                              activeItemInput={activeItemInput}
                              setActiveItemInput={setActiveItemInput}
                              setShowItemDialog={setShowItemDialog}
                              updateSectionName={updateSectionName}
                              removeSection={removeSection}
                              addLineItem={addLineItem}
                              updateLineItem={updateLineItem}
                              selectItemFromLibrary={selectItemFromLibrary}
                              removeLineItem={removeLineItem}
                            />
                          ))}
                        </div>
                      </SortableContext>
                      <DragOverlay>
                        {activeDragId && !activeDragId.startsWith('section-') && (
                          <div className="bg-background border rounded shadow-lg p-2 text-sm">
                            Dragging item...
                          </div>
                        )}
                        {activeDragId && activeDragId.startsWith('section-') && (
                          <div className="bg-background border rounded shadow-lg p-2 text-sm">
                            Dragging section...
                          </div>
                        )}
                      </DragOverlay>
                    </DndContext>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addSection()}
                        className="text-sm"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add section
                      </Button>
                    </div>
                  </motion.div>

                  {/* Totals */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="border-t pt-4 space-y-2"
                  >
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatAmount(subtotal)}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Markup</span>
                      <button
                        type="button"
                        onClick={() => setShowMarkupModal(true)}
                        className="text-purple-600 hover:text-purple-700 hover:underline text-sm font-medium"
                      >
                        {getMarkupDisplayText()}
                      </button>
                    </div>

                    {shippingFee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Shipping</span>
                        <span>+{formatAmount(shippingFee)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-lg pt-2 border-t">
                      <span>Total</span>
                      <span>{formatAmount(total)}</span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

          {/* Right: Fixed Sidebar */}
          {phase === 'full-form' && (
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 25 }}
              className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
            >
              {/* Save status */}
              <div className="px-5 py-4 flex items-center justify-end">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : lastSaved ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" />
                      <span>Saved</span>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 space-y-5">
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Settings</h2>

                  {/* Shipping */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <Label className="text-muted-foreground">Shipping</Label>
                      </div>
                      <Switch
                        checked={shipEnabled}
                        onCheckedChange={setShipEnabled}
                      />
                    </div>

                    {shipEnabled && (
                      <div className="space-y-3 pt-2">
                        {/* Ship Fee */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Shipping Fee</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                              {getCurrencySymbol()}
                            </span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={shipFee ?? ''}
                              onChange={(e) => setShipFee(e.target.value ? parseFloat(e.target.value) : null)}
                              placeholder="0.00"
                              className="pl-7"
                            />
                          </div>
                        </div>

                        {/* Ship Date */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Ship Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start text-left font-normal"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {shipDate ? format(new Date(shipDate + 'T00:00:00'), 'PPP') : 'Select date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={shipDate ? new Date(shipDate + 'T00:00:00') : undefined}
                                onSelect={(date) => date && setShipDate(date.toISOString().split('T')[0])}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        {/* Ship Via */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Ship Via</Label>
                          <Input
                            value={shipVia}
                            onChange={(e) => setShipVia(e.target.value)}
                            placeholder="e.g., UPS, FedEx, USPS"
                          />
                        </div>

                        {/* Tracking Number */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Tracking Number</Label>
                          <Input
                            value={shipTrackingNumber}
                            onChange={(e) => setShipTrackingNumber(e.target.value)}
                            placeholder="Enter tracking number"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Attachments */}
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Attachments</Label>

                    {purchaseOrder?.id && Array.isArray(purchaseOrder.attachments) && purchaseOrder.attachments.length > 0 ? (
                      <div className="space-y-2">
                        {purchaseOrder.attachments.map((att, index) => (
                          <div key={`${purchaseOrder.id}-${att.url || index}`} className="flex items-center justify-between p-2 border rounded-lg bg-background">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs truncate">{att.filename}</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 flex-shrink-0"
                              onClick={() => handleRemoveAttachment(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div
                      className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer bg-background ${uploadingAttachment ? 'opacity-50' : 'hover:border-primary'}`}
                      onClick={() => !uploadingAttachment && fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files)}
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      {uploadingAttachment ? (
                        <Loader2 className="h-4 w-4 text-muted-foreground mx-auto mb-1 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 text-muted-foreground mx-auto mb-1" />
                      )}
                      <p className="text-xs text-muted-foreground">
                        {uploadingAttachment ? 'Uploading...' : 'Click to upload'}
                      </p>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Note for Vendor</Label>
                    <Textarea
                      placeholder="Visible to the vendor..."
                      value={noteForVendor}
                      onChange={(e) => setNoteForVendor(e.target.value)}
                      rows={3}
                      className="resize-none bg-background"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Internal Notes</Label>
                    <Textarea
                      placeholder="Not visible to vendor..."
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      rows={3}
                      className="resize-none bg-background"
                    />
                  </div>
                </div>

                {/* Fixed action buttons at bottom */}
                <div className="p-4 border-t bg-background space-y-3">
                  {purchaseOrder?.status === 'draft' && (
                    <Button
                      type="button"
                      onClick={handleSend}
                      disabled={!purchaseOrder}
                      className="w-full"
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Send
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      if (!purchaseOrder) return;
                      await saveNow();
                      nav(`/purchase-orders/${purchaseOrder.id}`);
                    }}
                    disabled={!purchaseOrder}
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              </motion.div>
          )}
        </div>
      </div>

      {/* Vendor Dialog for creating new vendor (Phase 1) */}
      {businessId && (
        <ClientDialog
          open={showVendorDialog}
          onClose={() => setShowVendorDialog(false)}
          businessId={businessId}
          onSaved={showChangeVendorDialog ? handleVendorSavedForChange : handleVendorSaved}
        />
      )}

      {/* Change Vendor Dialog (Phase 2) */}
      {showChangeVendorDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Change Vendor</h2>
              <p className="text-sm text-muted-foreground">Select a different vendor for this purchase order.</p>
            </div>

            <div className="p-4 space-y-4 flex-1 overflow-hidden flex flex-col">
              <div className="flex gap-2">
                <Input
                  placeholder="Search vendors..."
                  value={changeVendorSearchQuery}
                  onChange={(e) => setChangeVendorSearchQuery(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowChangeVendorDialog(false);
                    setShowVendorDialog(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-2">
                {filteredVendorsForChange.map((vendor) => (
                  <div
                    key={vendor.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      vendor.id === selectedVendor?.id
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleChangeVendor(vendor)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{vendor.billing_name}</p>
                        {vendor.company_name && (
                          <p className="text-xs text-muted-foreground">{vendor.company_name}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangeVendorDialog(false);
                  setChangeVendorSearchQuery('');
                }}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Item Dialog */}
      {businessId && (
        <ItemDialog
          open={showItemDialog}
          onClose={() => setShowItemDialog(false)}
          businessId={businessId}
          onSaved={handleItemSaved}
        />
      )}

      {/* Markup Modal */}
      <MarkupModal
        open={showMarkupModal}
        onOpenChange={setShowMarkupModal}
        markupType={markupType}
        markupValue={markupValue}
        markupAppliedTo={markupAppliedTo}
        applicableSubtotals={getApplicableSubtotals()}
        onSave={(type, value, appliedTo) => {
          setMarkupType(type);
          setMarkupValue(value);
          setMarkupAppliedTo(appliedTo);
        }}
        currencySymbol={getCurrencySymbol()}
        formatAmount={formatAmount}
      />

      {/* Cancel Draft Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <button
            onClick={() => setShowDiscardDialog(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            disabled={discardingDraft}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          <AlertDialogHeader>
            <AlertDialogTitle>Save or Discard?</AlertDialogTitle>
            <AlertDialogDescription>
              {startedAsNew.current
                ? 'Would you like to save this purchase order as a draft or discard it?'
                : 'Would you like to save your changes or discard them?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={async () => {
                await saveNow();
                // New document → list, existing → detail page
                if (isEditMode && purchaseOrder) {
                  nav(`/purchase-orders/${purchaseOrder.id}`);
                } else {
                  nav('/purchase-orders');
                }
              }}
              disabled={discardingDraft}
            >
              {startedAsNew.current ? 'Save Draft' : 'Save Changes'}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={handleDiscard}
              disabled={discardingDraft}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {discardingDraft ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Discarding...
                </>
              ) : (
                'Discard'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
